import { creerClient, ErreurApi, type Client, type OptionsClient } from '../../api/client.js';
import { transferer, type StockageLocal, type TransportTransfert } from './transfert.js';

/**
 * Envoi reprenable d'une pièce par l'héritier (M05). Deux temps : on ouvre la session côté serveur par la route du portail
 * `portail.pieces.deposer` (une seule fois par envoi : la session est gardée sur l'appareil), puis les octets partent par
 * morceaux (transfert.ts, protocole de @lakou/sync), en repartant du dernier morceau acquitté.
 *
 * Le portail ne choisit ni le dossier, ni le titre, ni la catégorie, ni les liens de la pièce : il désigne seulement la pièce
 * attendue (`requirementId`) ; le serveur en déduit le reste. La clé de l'envoi sert d'Idempotency-Key à l'ouverture de la
 * session : une réponse perdue ou une session expirée, puis une nouvelle tentative, retombent sur la MÊME session côté serveur.
 */
export const PREFIXE_SESSION_PIECE = 'piece-session:';

/** Réponse de `portail.pieces.deposer` (`PortalUploadSession`) : aucun identifiant de document n'est rendu au portail. */
interface SessionPortail {
  uploadUrl: string;
  expiresAt: string;
  maxSizeBytes: number;
}

/**
 * Client dont l'Idempotency-Key est la clé de l'envoi. Le client de la coquille tire une clé au hasard à chaque appel ;
 * en attendant l'option `cleIdempotence` de `EntreeAppel` (RFC-L58-002 §1, L50), on le fabrique avec la clé imposée.
 */
export const clientAvecCle = (options: OptionsClient, cle: string): Client => creerClient({ ...options, genererCle: () => cle });

export interface OptionsEnvoiPiece {
  client: Client;
  /** Fabrique le client de l'envoi à partir de sa clé (voir `clientAvecCle`). À défaut, `client` est utilisé tel quel. */
  clientPourCle?: (cle: string) => Client;
  /** Pièce attendue que l'héritier envoie (`DocumentRequirement.id`). */
  requirementId: string;
  /** Clé d'idempotence de l'envoi, stable d'une tentative à l'autre. */
  cle: string;
  fichier: { nom: string; mime: string; octets: Uint8Array };
  stockage: StockageLocal;
  fabriquerTransport: (uploadUrl: string) => TransportTransfert;
  tailleMorceau?: number;
  surProgres?: (acquis: number, total: number) => void;
  maintenant?: () => Date;
}

export interface BilanEnvoiPiece {
  /** `trop_lourd` : la photo dépasse la taille que le serveur accepte (`maxSizeBytes`) ; rien n'est envoyé. */
  etat: 'termine' | 'interrompu' | 'corrompu' | 'trop_lourd' | 'echec';
  envoyes: number;
  total: number;
}

export async function envoyerPiece(options: OptionsEnvoiPiece): Promise<BilanEnvoiPiece> {
  const { client, requirementId, cle, fichier, stockage, maintenant = () => new Date() } = options;
  const total = fichier.octets.length;
  const cleSession = `${PREFIXE_SESSION_PIECE}${cle}`;

  let session = await stockage.lire<SessionPortail>(cleSession);
  if (session === undefined || Date.parse(session.expiresAt) <= maintenant().getTime()) {
    try {
      session = await (options.clientPourCle?.(cle) ?? client).appeler<SessionPortail>('portail.pieces.deposer', {
        corps: { requirementId, mime: fichier.mime, size: total },
      });
    } catch (erreur) {
      if (erreur instanceof ErreurApi && erreur.code === 'RESEAU') return { etat: 'interrompu', envoyes: 0, total };
      throw erreur;
    }
    if (total > session.maxSizeBytes) {
      await stockage.supprimer(cleSession);
      return { etat: 'trop_lourd', envoyes: 0, total };
    }
    await stockage.ecrire(cleSession, session);
  }

  const bilan = await transferer({
    stockage,
    transport: options.fabriquerTransport(session.uploadUrl),
    cle,
    nom: fichier.nom,
    type: fichier.mime,
    octets: fichier.octets,
    ...(options.tailleMorceau === undefined ? {} : { tailleMorceau: options.tailleMorceau }),
    surProgres: (acquis) => options.surProgres?.(acquis, total),
  });
  if (bilan.etat !== 'interrompu') await stockage.supprimer(cleSession);
  return { etat: bilan.etat, envoyes: bilan.envoyes, total };
}
